import React, { useEffect, useRef, useState, useCallback } from "react";
import { Loader2, ChevronLeft, ChevronRight, Clock, RotateCcw, BookMarked, Pencil, Check, X } from "lucide-react";
import api from "@/lib/api";
import { Wordmark } from "@/components/Wordmark";
import { SlideView } from "@/components/SlideView";
import { StagePreviewContext } from "@/components/Stage";
import { getMergedNotes, hasOverride, saveNotes, resetNotes } from "@/lib/presenterNotes";

const CHANNEL = "lexafide-deck";

// Renders a live slide thumbnail that scales to its container width.
const Frame = ({ manifest, isAppendix, index, aIndex }) => {
  const ref = useRef(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setW(el.clientWidth));
    ro.observe(el);
    setW(el.clientWidth);
    return () => ro.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className="w-full rounded-xl overflow-hidden border border-hairline bg-[#F7F6F5] shadow-[0_10px_30px_-18px_rgba(35,31,35,0.4)]"
      style={{ aspectRatio: "16 / 9" }}
    >
      {w > 0 ? (
        <StagePreviewContext.Provider value={{ scale: w / 1600 }}>
          <SlideView
            manifest={manifest}
            isAppendix={isAppendix}
            index={index}
            aIndex={aIndex}
            demoCall={null}
          />
        </StagePreviewContext.Provider>
      ) : null}
    </div>
  );
};

const fmtTime = (s) => {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, "0")}`;
};

export const PresenterView = () => {
  const [manifest, setManifest] = useState(null);
  const [error, setError] = useState(false);
  const [view, setView] = useState("main");
  const [index, setIndex] = useState(0);
  const [aIndex, setAIndex] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const chanRef = useRef(null);
  const [editing, setEditing] = useState(false);
  const [draftTitle, setDraftTitle] = useState("");
  const [draftPoints, setDraftPoints] = useState("");
  const [rev, setRev] = useState(0); // bump to recompute notes after save/reset

  useEffect(() => {
    api
      .get("/deck/manifest")
      .then((res) => setManifest(res.data))
      .catch(() => setError(true));
  }, []);

  // Timer
  useEffect(() => {
    const t = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(t);
  }, []);

  // Sync channel
  useEffect(() => {
    const ch = new BroadcastChannel(CHANNEL);
    chanRef.current = ch;
    ch.onmessage = (ev) => {
      const m = ev.data || {};
      if (m.type === "state") {
        setView(m.view);
        setIndex(m.index);
        setAIndex(m.aIndex);
      }
    };
    ch.postMessage({ type: "request-state" });
    return () => ch.close();
  }, []);

  const send = useCallback((msg) => {
    chanRef.current?.postMessage(msg);
  }, []);

  // Leave edit mode when the slide changes so drafts never bleed across slides.
  useEffect(() => {
    setEditing(false);
  }, [view, index, aIndex]);

  // Tell the main deck when this window closes so it can drop the indicator.
  useEffect(() => {
    const bye = () => chanRef.current?.postMessage({ type: "presenter-bye" });
    window.addEventListener("pagehide", bye);
    return () => {
      bye();
      window.removeEventListener("pagehide", bye);
    };
  }, []);

  const nav = useCallback((delta) => send({ type: "nav", delta }), [send]);
  const toggleAppendix = useCallback(() => send({ type: "toggle-appendix" }), [send]);

  useEffect(() => {
    const onKey = (e) => {
      const tag = e.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || e.target?.isContentEditable) return;
      if (e.key === "ArrowRight" || e.key === "PageDown" || e.key === " ") {
        e.preventDefault();
        nav(1);
      } else if (e.key === "ArrowLeft" || e.key === "PageUp") {
        e.preventDefault();
        nav(-1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [nav]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F6F5] p-8 text-center">
        <div>
          <p className="font-display text-2xl text-ink mb-2">Presenter view needs the deck open.</p>
          <p className="font-sans text-sm text-ink-soft">
            Log in on the main deck window first, then reopen this with ⌘/Ctrl + L.
          </p>
        </div>
      </div>
    );
  }
  if (!manifest) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F6F5]">
        <Loader2 className="w-6 h-6 animate-spin text-ink-soft" />
      </div>
    );
  }

  const total = manifest.slides?.length || 0;
  const appendix = manifest.appendix || [];
  const isAppendix = view === "appendix";

  // Determine "next" slide within the current view.
  let nextExists = false;
  let nextProps = null;
  if (isAppendix) {
    if (aIndex < appendix.length - 1) {
      nextExists = true;
      nextProps = { isAppendix: true, index: 0, aIndex: aIndex + 1 };
    }
  } else if (index < total - 1) {
    nextExists = true;
    nextProps = { isAppendix: false, index: index + 1, aIndex: 0 };
  }

  const notes = getMergedNotes(view, index, aIndex);
  const edited = hasOverride(view, index, aIndex);
  const counter = isAppendix
    ? `Appendix ${aIndex + 1} / ${appendix.length}`
    : `${index + 1} / ${total}`;

  void rev; // notes recompute keyed off rev bumps

  const startEdit = () => {
    setDraftTitle(notes.title);
    setDraftPoints(notes.points.join("\n"));
    setEditing(true);
  };
  const saveEdit = () => {
    saveNotes(view, index, aIndex, draftTitle, draftPoints.split("\n"));
    setEditing(false);
    setRev((r) => r + 1);
  };
  const revertEdit = () => {
    resetNotes(view, index, aIndex);
    setEditing(false);
    setRev((r) => r + 1);
  };

  return (
    <div className="h-screen w-screen bg-[#F2F1EF] text-ink flex flex-col overflow-hidden">
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-hairline bg-[#F7F6F5] shrink-0">
        <div className="flex items-center gap-3">
          <Wordmark size="sm" />
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted border-l border-hairline pl-3">
            Presenter view · private
          </span>
        </div>
        <div className="flex items-center gap-4">
          <div className="inline-flex items-center gap-2 font-mono text-sm text-ink-soft tabular-nums">
            <Clock className="w-4 h-4 text-accent" />
            {fmtTime(elapsed)}
            <button
              data-testid="presenter-timer-reset"
              onClick={() => setElapsed(0)}
              className="ml-1 text-ink-muted hover:text-ink transition-colors"
              aria-label="Reset timer"
            >
              <RotateCcw className="w-3.5 h-3.5" />
            </button>
          </div>
          <span className="font-mono text-xs tracking-[0.24em] text-ink-muted tabular-nums">
            {counter}
          </span>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 min-h-0 grid grid-cols-[1.55fr_1fr] gap-6 p-6">
        {/* Left: current slide + next */}
        <div className="flex flex-col min-h-0 gap-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-accent mb-2">
              On screen now
            </div>
            <Frame manifest={manifest} isAppendix={isAppendix} index={index} aIndex={aIndex} />
          </div>
          <div className="flex items-end gap-4 mt-auto">
            <div className="w-[38%] shrink-0">
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-muted mb-2">
                Up next
              </div>
              {nextExists ? (
                <Frame
                  manifest={manifest}
                  isAppendix={nextProps.isAppendix}
                  index={nextProps.index}
                  aIndex={nextProps.aIndex}
                />
              ) : (
                <div
                  className="w-full rounded-xl border border-dashed border-hairline flex items-center justify-center text-ink-muted font-mono text-[10px] uppercase tracking-[0.16em]"
                  style={{ aspectRatio: "16 / 9" }}
                >
                  End of {isAppendix ? "appendix" : "deck"}
                </div>
              )}
            </div>
            <div className="flex-1">
              {nextExists ? (
                <p className="font-sans text-[13px] text-ink-soft leading-snug">
                  Next:{" "}
                  <span className="font-semibold text-ink">
                    {getMergedNotes(
                      nextProps.isAppendix ? "appendix" : "main",
                      nextProps.index,
                      nextProps.aIndex
                    ).title}
                  </span>
                </p>
              ) : null}
              <div className="flex items-center gap-2 mt-3">
                <button
                  data-testid="presenter-prev"
                  onClick={() => nav(-1)}
                  className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-hairline hover:bg-ink hover:text-paper-white transition-colors"
                  aria-label="Previous slide"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  data-testid="presenter-next"
                  onClick={() => nav(1)}
                  className="inline-flex items-center gap-2 rounded-full bg-ink text-paper-white px-5 h-10 font-sans text-sm font-medium hover:bg-accent transition-colors"
                >
                  Next <ChevronRight className="w-4 h-4" />
                </button>
                <button
                  data-testid="presenter-appendix"
                  onClick={toggleAppendix}
                  className="inline-flex items-center gap-1.5 ml-1 font-mono text-[10px] uppercase tracking-[0.16em] text-ink-soft hover:text-ink transition-colors"
                >
                  <BookMarked className="w-3.5 h-3.5" /> {isAppendix ? "Deck" : "Appendix"}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Right: notes */}
        <div
          data-testid="presenter-notes"
          className="rounded-2xl bg-[linear-gradient(160deg,#191622_0%,#211D2A_100%)] text-paper p-6 flex flex-col min-h-0"
        >
          <div className="flex items-center justify-between mb-1">
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#93B4FF] flex items-center gap-2">
              Talking points
              {edited ? (
                <span className="text-[9px] tracking-[0.14em] text-accent border border-accent/40 rounded-full px-1.5 py-0.5">
                  edited
                </span>
              ) : null}
            </div>
            {!editing ? (
              <button
                data-testid="presenter-notes-edit"
                onClick={startEdit}
                className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-paper/50 hover:text-paper transition-colors"
              >
                <Pencil className="w-3 h-3" /> Edit
              </button>
            ) : (
              <div className="flex items-center gap-3">
                <button
                  data-testid="presenter-notes-save"
                  onClick={saveEdit}
                  className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-[#8FE3B0] hover:text-white transition-colors"
                >
                  <Check className="w-3.5 h-3.5" /> Save
                </button>
                <button
                  data-testid="presenter-notes-cancel"
                  onClick={() => setEditing(false)}
                  className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-paper/50 hover:text-paper transition-colors"
                >
                  <X className="w-3.5 h-3.5" /> Cancel
                </button>
              </div>
            )}
          </div>

          {editing ? (
            <div className="flex-1 min-h-0 flex flex-col">
              <input
                data-testid="presenter-notes-title-input"
                value={draftTitle}
                onChange={(e) => setDraftTitle(e.target.value)}
                placeholder="Slide title"
                className="w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2 mb-3 font-display text-lg text-paper placeholder:text-paper/30 focus:outline-none focus:border-accent"
              />
              <textarea
                data-testid="presenter-notes-points-input"
                value={draftPoints}
                onChange={(e) => setDraftPoints(e.target.value)}
                placeholder="One talking point per line…"
                className="flex-1 min-h-0 w-full resize-none bg-white/5 border border-white/15 rounded-lg px-3 py-2 font-sans text-[15px] leading-relaxed text-paper/90 placeholder:text-paper/30 focus:outline-none focus:border-accent"
              />
              <div className="mt-3 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.14em] text-paper/40">
                <span>One point per line</span>
                {edited ? (
                  <button
                    data-testid="presenter-notes-reset"
                    onClick={revertEdit}
                    className="inline-flex items-center gap-1.5 text-paper/50 hover:text-paper transition-colors"
                  >
                    <RotateCcw className="w-3 h-3" /> Reset to default
                  </button>
                ) : null}
              </div>
            </div>
          ) : (
            <>
              <h2 className="font-display font-medium text-xl leading-tight text-paper mb-4">
                {notes.title}
              </h2>
              <div className="flex-1 min-h-0 overflow-y-auto space-y-3 pr-1">
                {notes.points.length ? (
                  notes.points.map((p, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <span className="w-1.5 h-1.5 rounded-full bg-accent mt-[9px] shrink-0" />
                      <span className="font-sans text-[15px] leading-relaxed text-paper/85">{p}</span>
                    </div>
                  ))
                ) : (
                  <p className="font-sans text-sm text-paper/50">No notes for this slide.</p>
                )}
              </div>
              <div className="mt-4 pt-3 border-t border-white/10 font-mono text-[10px] uppercase tracking-[0.14em] text-paper/40">
                Only you can see this — share the deck window, not your screen.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PresenterView;
